/**
 * IncidentDetailPanel -- Detail view for a single incident.
 *
 * Shows: narrative summary, phases, participants, recommended actions,
 * full conversation timeline (evidence + context), and incident actions.
 */

import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import {
  X, Brain, MessageSquare, ExternalLink, AlertTriangle,
  Users, ListChecks, Clock, ShieldAlert, MessageCircle,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { useIsMobile } from '@/hooks/use-mobile';
import { useIncidentTimeline } from '@/hooks/useIncidentTimeline';
import { IncidentActions } from '@/components/IncidentActions';
import { IncidentTimeline } from '@/components/IncidentTimeline';
import {
  type Incident,
  type TimelineMessage,
  getStatusConfig,
  getSeverityConfig,
  formatIncidentAge,
  getScoreColorClass,
  isTerminalStatus,
} from '@/lib/incidentUtils';

interface IncidentDetailPanelProps {
  incident: Incident;
  onClose: () => void;
  onResolve: (notes: string) => Promise<void>;
  onDismiss: (notes: string) => Promise<void>;
  onEscalate: (escalateTo: string) => Promise<void>;
}

const ROLE_STYLES: Record<string, string> = {
  instigator: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  victim: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  intervener: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  bystander: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
};

function IncidentDetailContent({
  incident,
  onClose,
  onResolve,
  onDismiss,
  onEscalate,
}: IncidentDetailPanelProps) {
  const navigate = useNavigate();
  const statusConfig = getStatusConfig(incident.status);
  const severityConfig = getSeverityConfig(incident.severity);

  // Load full timeline (evidence + context messages)
  const { timeline, loading: timelineLoading } = useIncidentTimeline(
    incident.connection_id,
    incident.conversation_id,
    incident.id,
  );

  const narrative = incident.narrative;
  // Show only evidence (key) messages in incident detail — full context lives in conversation view
  const evidenceMessages = timeline.filter(m => m.context_type === 'evidence');
  const hasTimeline = timeline.length > 0;
  const evidenceCount = evidenceMessages.length;
  const contextCount = timeline.length - evidenceCount;

  // Convert evidence-only timeline messages to IncidentMessage format for the timeline component
  const timelineAsMessages = evidenceMessages.map(t => ({
    message_id: t.message_id,
    timestamp: t.timestamp,
    flagged_categories: t.flagged_categories,
    severity_score: t.severity_score,
    content: t.content,
    messageType: t.messageType as 'text' | 'image' | 'video' | 'audio' | 'document' | 'sticker',
    pushName: t.pushName,
    fromMe: t.fromMe,
    mediaUrl: t.mediaUrl,
    context_type: t.context_type,
    event_explanation: t.event_explanation,
  }));

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge className={`text-xs ${severityConfig.className}`}>
              {severityConfig.label}
            </Badge>
            <Badge variant="outline" className={`text-xs ${statusConfig.className}`}>
              {statusConfig.label}
            </Badge>
            {incident.context.conversation_name && (
              <span className="text-sm font-medium text-muted-foreground">
                {incident.context.conversation_name}
              </span>
            )}
          </div>
          <div className="flex gap-4 text-xs text-muted-foreground">
            <span>Created {formatIncidentAge(incident.created_at)}</span>
            <span>Updated {format(incident.updated_at, 'MMM d, HH:mm')}</span>
            {incident.message_count > 0 && (
              <span>{incident.message_count} flagged{contextCount > 0 ? `, ${contextCount} context` : ''}</span>
            )}
          </div>
          <Button
            variant="outline"
            size="sm"
            className="mt-1"
            disabled={!incident.connection_id || !incident.conversation_id}
            onClick={() => navigate(`/${incident.connection_id}/conversations/${incident.conversation_id}`)}
          >
            <ExternalLink className="h-3 w-3 mr-1" />
            View Conversation
          </Button>
        </div>
        <Button variant="ghost" size="sm" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      <Separator />

      {/* Narrative Summary (replaces generic AI Analysis) */}
      {narrative?.summary ? (
        <Card className="bg-muted/50">
          <CardHeader className="py-3 px-4">
            <CardTitle className="text-sm flex items-center gap-2">
              <Brain className="h-4 w-4" />
              Incident Summary
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-3 space-y-3">
            <p className="text-sm">{narrative.summary}</p>

            {/* Severity Assessment */}
            {narrative.severity_assessment && (
              <div className="flex items-start gap-2 text-sm">
                <AlertTriangle className="h-4 w-4 mt-0.5 text-orange-500 shrink-0" />
                <p className="text-muted-foreground">{narrative.severity_assessment}</p>
              </div>
            )}
          </CardContent>
        </Card>
      ) : incident.context.reasoning ? (
        // Fallback to per-message reasoning if no narrative yet
        <Card className="bg-muted/50">
          <CardHeader className="py-3 px-4">
            <CardTitle className="text-sm flex items-center gap-2">
              <Brain className="h-4 w-4" />
              AI Analysis
              {incident.context.confidence > 0 && (
                <Badge variant="outline" className="text-xs ml-auto">
                  {Math.round(incident.context.confidence * 100)}% confidence
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-3">
            <p className="text-sm">{incident.context.reasoning}</p>
          </CardContent>
        </Card>
      ) : null}

      {/* Narrative Phases */}
      {narrative && narrative.phases.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium flex items-center gap-2">
            <Clock className="h-4 w-4" />
            Incident Phases
          </h4>
          <div className="space-y-2">
            {narrative.phases.map((phase) => (
              <Card key={phase.number} className="p-3">
                <div className="flex items-center gap-2 mb-1">
                  <Badge variant="outline" className="text-xs">Phase {phase.number}</Badge>
                  <span className="text-sm font-medium">{phase.title}</span>
                </div>
                <p className="text-sm text-muted-foreground">{phase.description}</p>
                {phase.key_participants.length > 0 && (
                  <div className="flex gap-1 mt-1.5">
                    {phase.key_participants.map((name, i) => (
                      <Badge key={i} variant="secondary" className="text-xs">{name}</Badge>
                    ))}
                  </div>
                )}
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Participants */}
      {narrative && narrative.participants.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium flex items-center gap-2">
            <Users className="h-4 w-4" />
            Participants
          </h4>
          <div className="grid gap-2 grid-cols-1 sm:grid-cols-2">
            {narrative.participants.map((p, i) => (
              <Card key={i} className="p-3">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-medium">{p.name}</span>
                  <Badge className={`text-xs ${ROLE_STYLES[p.role] ?? ROLE_STYLES.bystander}`}>
                    {p.role}
                  </Badge>
                </div>
                {p.key_actions.length > 0 && (
                  <ul className="text-xs text-muted-foreground list-disc list-inside">
                    {p.key_actions.map((action, j) => (
                      <li key={j}>{action}</li>
                    ))}
                  </ul>
                )}
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Recommended Actions */}
      {narrative && narrative.recommended_actions.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium flex items-center gap-2">
            <ListChecks className="h-4 w-4" />
            Recommended Actions
          </h4>
          <Card className="p-3">
            <ul className="text-sm space-y-1">
              {narrative.recommended_actions.map((action, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="text-muted-foreground mt-0.5">•</span>
                  <span>{action}</span>
                </li>
              ))}
            </ul>
          </Card>
        </div>
      )}

      {/* Flagged Categories with Scores */}
      {Object.keys(incident.context.moderation_categories).length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium">Flagged Categories</h4>
          <div className="flex flex-wrap gap-1">
            {Object.entries(incident.context.moderation_categories)
              .sort(([, a], [, b]) => b - a)
              .map(([category, score]) => (
                <Badge key={category} className={`text-xs ${getScoreColorClass(score)}`}>
                  {category} {(score * 100).toFixed(0)}%
                </Badge>
              ))}
          </div>
        </div>
      )}

      {/* Full Conversation Timeline (evidence + context) */}
      <div className="space-y-2">
        <h4 className="text-sm font-medium flex items-center gap-2">
          <ShieldAlert className="h-4 w-4" />
          {hasTimeline ? (
            <>Key Evidence ({evidenceCount})</>
          ) : (
            <>Flagged Messages ({incident.messages.length})</>
          )}
          {hasTimeline && contextCount > 0 && (
            <span className="text-xs text-muted-foreground font-normal">
              {contextCount} context messages in conversation view
            </span>
          )}
        </h4>
        {timelineLoading ? (
          <div className="text-sm text-muted-foreground">Loading timeline...</div>
        ) : hasTimeline ? (
          <IncidentTimeline messages={timelineAsMessages} />
        ) : incident.messages.length > 0 ? (
          <IncidentTimeline messages={incident.messages} />
        ) : (
          <div className="text-sm text-muted-foreground">No messages in this incident.</div>
        )}
      </div>

      {/* Resolution Notes (for closed incidents) */}
      {isTerminalStatus(incident.status) && incident.resolution_notes && (
        <>
          <Separator />
          <div className="space-y-1">
            <h4 className="text-sm font-medium">Resolution Notes</h4>
            <p className="text-sm text-muted-foreground">{incident.resolution_notes}</p>
          </div>
        </>
      )}

      {/* Escalation Target */}
      {incident.status === 'ESCALATED' && incident.escalated_to && (
        <div className="space-y-1">
          <h4 className="text-sm font-medium">Escalated To</h4>
          <p className="text-sm text-muted-foreground">{incident.escalated_to}</p>
        </div>
      )}

      {/* Actions */}
      {!isTerminalStatus(incident.status) && (
        <>
          <Separator />
          <IncidentActions
            incident={incident}
            onResolve={onResolve}
            onDismiss={onDismiss}
            onEscalate={onEscalate}
          />
        </>
      )}
    </div>
  );
}

export function IncidentDetailPanel(props: IncidentDetailPanelProps) {
  const isMobile = useIsMobile();

  if (isMobile) {
    return (
      <Sheet open={true} onOpenChange={(open) => { if (!open) props.onClose(); }}>
        <SheetContent side="bottom" className="h-[85vh] overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Incident Details</SheetTitle>
          </SheetHeader>
          <div className="mt-4">
            <IncidentDetailContent {...props} />
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Card className="mb-3">
      <CardContent className="pt-4">
        <IncidentDetailContent {...props} />
      </CardContent>
    </Card>
  );
}
